import { useState } from "react";
import Layout from "../../../components/Layout";
import { HiPlus, HiPencil, HiTrash, HiSearch } from "react-icons/hi";
import LeavePolicyForm from "./LeavePolicyForm";
import { CustomButton, CustomInput } from "../../../components/FormFields";
import { StatusBadge } from "../../../components/StatusBadge";

const CAT_COLORS = ["purple", "blue", "indigo", "success", "warning"];

const initialPolicies = [
  {
    id: 1,
    name: "Permanent Staff Policy",
    description: "Standard leave entitlements for all permanent staff",
    appliesTo: ["Senior Staff", "Junior Staff"],
    entitlements: [
      { id: 1, name: "Annual Leave",    daysPerYear: 21 },
      { id: 2, name: "Sick Leave",      daysPerYear: 14 },
      { id: 3, name: "Casual Leave",    daysPerYear: 7  },
      { id: 4, name: "No-Pay Leave",    daysPerYear: 0  },
      { id: 5, name: "Maternity Leave", daysPerYear: 84 },
    ],
  },
  {
    id: 2,
    name: "Temporary Staff Policy",
    description: "Reduced leave for temporary and contract employees",
    appliesTo: ["Temporary Staff"],
    entitlements: [
      { id: 3, name: "Casual Leave",  daysPerYear: 5 },
      { id: 4, name: "No-Pay Leave",  daysPerYear: 0 },
    ],
  },
  {
    id: 3,
    name: "Intern Policy",
    description: "Minimal leave entitlement for interns and trainees",
    appliesTo: ["Intern"],
    entitlements: [
      { id: 3, name: "Casual Leave", daysPerYear: 3 },
    ],
  },
];

export default function LeavePolicy() {
  const [policies, setPolicies] = useState(initialPolicies);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [search, setSearch]     = useState("");

  const filtered = policies.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  function handleSave(policy) {
    setPolicies((prev) =>
      prev.some((p) => p.id === policy.id)
        ? prev.map((p) => (p.id === policy.id ? policy : p))
        : [...prev, policy]
    );
  }

  function handleDelete(id) {
    setPolicies((prev) => prev.filter((p) => p.id !== id));
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Leave Policies</h1>
            <p className="mt-1 text-sm text-gray-500">
              Group leave entitlements and assign them to employee categories
            </p>
          </div>
          <CustomButton color="purple" onClick={() => { setEditData(null); setShowForm(true); }}>
            <HiPlus className="mr-2 h-4 w-4" /> Create Policy
          </CustomButton>
        </div>

        {/* Search */}
        <div className="w-full max-w-sm">
          <CustomInput
            icon={HiSearch}
            placeholder="Search policies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Policy Cards */}
        {filtered.length === 0 ? (
          <p className="py-16 text-center text-sm text-gray-400">No policies found.</p>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
            {filtered.map((policy) => (
              <div
                key={policy.id}
                className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
              >
                {/* Card header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-white">{policy.name}</p>
                    {policy.description && (
                      <p className="mt-0.5 text-xs text-gray-400 line-clamp-2">{policy.description}</p>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 gap-1.5">
                    <CustomButton size="xs" color="blue" outline pill
                      onClick={() => { setEditData(policy); setShowForm(true); }}>
                      <HiPencil className="h-3.5 w-3.5" />
                    </CustomButton>
                    <CustomButton size="xs" color="failure" outline pill
                      onClick={() => handleDelete(policy.id)}>
                      <HiTrash className="h-3.5 w-3.5" />
                    </CustomButton>
                  </div>
                </div>

                {/* Applies To */}
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                    Applies To
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {policy.appliesTo.map((cat, i) => (
                      <StatusBadge key={cat} color={CAT_COLORS[i % CAT_COLORS.length]} className="text-xs">
                        {cat}
                      </StatusBadge>
                    ))}
                  </div>
                </div>

                {/* Entitlements list */}
                <div>
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                    Leave Entitlements ({policy.entitlements.length})
                  </p>
                  <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
                    {policy.entitlements.map((ent) => (
                      <div key={ent.id} className="flex items-center justify-between px-3 py-2">
                        <span className="text-xs text-gray-600 dark:text-gray-400">{ent.name}</span>
                        <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                          {ent.daysPerYear === 0 ? "Unlimited" : `${ent.daysPerYear} days/yr`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <LeavePolicyForm
          closeForm={() => { setShowForm(false); setEditData(null); }}
          initialData={editData}
          onSave={handleSave}
        />
      )}
    </Layout>
  );
}
